import { useRef, useEffect, useState } from 'react';
import RichText from 'components/shared/richtext';
import Button from 'components/shared/button';
import useIsMobile from 'hooks/utils/useIsMobile';
import useSubscriptionPrice from 'hooks/shop/useSubscriptionPrice';
import BuyingPros from 'components/shared/buyingPros';
import WishlistButton from 'components/shared/wishlist-button';
import classNames from 'classnames';
import UnitPrice from 'components/shared/unit-price/unitPrice';
import { useGlobalModules } from 'contexts';
import { formatPriceByStoreLocale } from 'lib/shopify';
import Rating from 'components/shared/reviewsIO/rating';
import AttributeTag from '../attributeTag/attributeTag';
import CartSection from '../cartSection/cartSection';
import styles from './productInfo.module.scss';
import HoldAndShine from '../holdAndShine/holdAndShine';
import ProductVariations from '../productVariations';
import Subscription from '../subscription/subscription';
import ProductAddon from '../productAddon';
import CartDiscountPrice from '../cartDiscountPrice';

const ProductInfo = ({ locale, productData }) => {
  const {
    title,
    description,
    productAttributes,
    productTags,
    linkToExternalWebshop,
    holdFactor,
    shineFactor,
    shopifyData,
    size,
    claimsLinks,
  } = productData;

  const globalModules = useGlobalModules();
  const isMobile = useIsMobile();
  const stickyCartSectionRef = useRef(null);
  const cartSectionRef = useRef(null);
  const [stickyCartSectionVisible, setStickyCartSectionVisible] = useState(false);
  const [sellingPlan, setSellingPlan] = useState(null);
  const [addonSelections, setAddonSelections] = useState({});

  const {
    onlyAvailableAtSalon,
    outOfStock: outOfStockMessage,
    dontShipToDestination,
    thisProductIs,
  } = globalModules.translations.product;
  const { hasWebshop, hasSalonFinder } = globalModules.settings.locale;
  const { saleText } = globalModules.translations.label;
  const { findASalonButton, contactUsButton } = globalModules.buttons.pdp;
  const { buyingPros } = globalModules.sections.pdp;
  const addonConfigs = globalModules.addon;
  const outOfStock = shopifyData?.outOfStock;
  const productPrice = shopifyData?.productPrice;
  const productCompareAtPrice = shopifyData?.productCompareAtPrice;
  const getSubscriptionPrice = useSubscriptionPrice(locale, shopifyData);
  const planPrice = sellingPlan ? getSubscriptionPrice(sellingPlan) : null;
  const hasPlanDiscount = planPrice !== null && planPrice < productPrice;
  const originalPriceLabel =
    globalModules.translations.accessibility?.originalPrice || 'Original price';
  const salePercentage = (100 - (productPrice / productCompareAtPrice) * 100).toFixed(0);
  const productHasLinkToAnotherProduct =
    productData?.linkToAnotherProduct?.slug && productData?.useLinkToAnotherProduct !== false;

  useEffect(() => {
    document.body.style.paddingBottom = isMobile && hasWebshop ? '6rem' : '';
    return () => {
      document.body.style.paddingBottom = '';
    };
  }, [isMobile, hasWebshop]);

  // detect if the user is scrolling down
  // needed to show/hide the add to cart button on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 900 && cartSectionRef.current) {
        const observer = new IntersectionObserver(([entry]) => {
          const { isIntersecting } = entry;
          setStickyCartSectionVisible(!isIntersecting);
        });

        observer.observe(cartSectionRef.current);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const AlternativeButtons = () => (
    <>
      {hasSalonFinder &&
        !hasWebshop &&
        !linkToExternalWebshop?.externalLink &&
        !productHasLinkToAnotherProduct && (
          <>
            <p className={styles.onlyAvailableAtSalonText}>{onlyAvailableAtSalon}</p>
            <Button locale={locale} className={styles.findASalonButton} button={findASalonButton} />
          </>
        )}
      {!hasSalonFinder && !hasWebshop && !linkToExternalWebshop?.externalLink && (
        <>
          <p className={styles.dontShipToDestinationText}>{dontShipToDestination}</p>
          <Button locale={locale} className={styles.contactUsButton} button={contactUsButton} />
        </>
      )}
      {!hasWebshop && linkToExternalWebshop?.externalLink && (
        <>
          <p className={styles.dontShipToDestinationText}>{dontShipToDestination}</p>
          <Button
            locale={locale}
            className={styles.findASalonButton}
            button={linkToExternalWebshop}
          />
        </>
      )}
    </>
  );

  const priceRow = (
    <div className={styles.priceRow}>
      <div className={styles.productPrice}>
        {hasPlanDiscount ? (
          <>
            <span className={styles.planPrice}>
              {formatPriceByStoreLocale({ locale, price: planPrice })}
            </span>
            <s>
              <span className="sr-only">{originalPriceLabel} </span>
              {formatPriceByStoreLocale({ locale, price: productPrice })}
            </s>
          </>
        ) : (
          formatPriceByStoreLocale({ locale, price: productPrice })
        )}
      </div>
      {!isMobile && (
        <Rating SKU={shopifyData?.productSKU} locale={locale} className={styles.rating} />
      )}
      {isMobile && (
        <UnitPrice
          unitPrice={shopifyData.unitPrice}
          price={productPrice}
          locale={locale}
          className={styles.unitPrice}
          size={size}
        />
      )}
    </div>
  );

  return (
    <div>
      {hasWebshop && !outOfStock && (
        <div className={styles.productTagsRow}>
          {productCompareAtPrice && (
            <div className={styles.salesTag}>{`${saleText || ''} ${salePercentage} %`}</div>
          )}
          {productTags?.map(tag => (
            <div key={`Product tag ${tag}`} className={styles.productTag}>
              {tag}
            </div>
          ))}
        </div>
      )}
      {hasWebshop && outOfStock && (
        <div className={styles.productTagsRow}>
          <div className={styles.outOfStockTag}>{outOfStockMessage}</div>
        </div>
      )}
      <div className={styles.productHeader}>
        <div className={styles.productHeaderContainer}>
          <h1 className={styles.productName}>{title}</h1>
          {hasWebshop && !isMobile && (
            <WishlistButton
              className={styles.wishlistIcon}
              productData={productData}
              shopifyData={productData.shopifyData}
            />
          )}
        </div>
       
        {hasWebshop &&
          shopifyData?.productPrice &&
          (addonConfigs?.length ? (
            <CartDiscountPrice
              shopifyData={shopifyData}
              locale={locale}
              addonConfigs={addonConfigs}
            >
              {priceRow}
            </CartDiscountPrice>
          ) : (
            priceRow
          ))}
        {isMobile && ((hasWebshop && productPrice) || productHasLinkToAnotherProduct) && (
          <div ref={cartSectionRef}>
            <CartSection
              locale={locale}
              className={isMobile}
              productData={productData}
              shopifyData={shopifyData}
              sellingPlan={sellingPlan}
              addonSelections={addonSelections}
            />
          </div>
        )}
        {isMobile && <AlternativeButtons />}
      </div>

      {!isMobile && !(hasWebshop && shopifyData?.productPrice) && (
        <Rating SKU={shopifyData?.productSKU} locale={locale} />
      )}

      <RichText className={styles.productDescription} text={description?.json} />

      {productAttributes && (
        <>
          <div className={styles.productAttributes}>
            {productAttributes.map(attribute => (
              <AttributeTag className={styles.attributeTag} key={attribute} link={{ title: attribute }} locale={locale} />
            ))}
          </div>
          <div className={styles.divider} />
        </>
      )}

      {holdFactor || shineFactor ? (
        <>
          <HoldAndShine holdFactor={holdFactor} shineFactor={shineFactor} />
          <div className={styles.divider} />
        </>
      ) : null}

      {hasWebshop && (
        <ProductAddon
          productData={productData}
          locale={locale}
          addonConfigs={addonConfigs}
          selections={addonSelections}
          onToggle={(variantId, checked) => {
            setAddonSelections(prev => ({ ...prev, [variantId]: checked }));
          }}
        />
      )}

      <ProductVariations locale={locale} productData={productData} />
      {!isMobile && (
        <UnitPrice
          unitPrice={shopifyData.unitPrice}
          price={shopifyData.productPrice}
          locale={locale}
          className={styles.unitPrice}
          size={size}
        />
      )}

      <Subscription
        shopifyData={shopifyData}
        locale={locale}
        sellingPlan={sellingPlan}
        setSellingPlan={setSellingPlan}
      />

      <div ref={stickyCartSectionRef}>
        {((hasWebshop && productPrice) || productHasLinkToAnotherProduct) && (
          <div
            className={classNames({
              [styles.fixedCartSectionWrapper]: isMobile,
              [styles.fixedCartSectionWrapperVisible]: isMobile && stickyCartSectionVisible,
            })}
          >
            <CartSection
              locale={locale}
              className={isMobile && styles.fixedCartSection}
              productData={productData}
              shopifyData={shopifyData}
              sellingPlan={sellingPlan}
              addonSelections={addonSelections}
            />
          </div>
        )}
        {!isMobile && <AlternativeButtons />}
      </div>

      {buyingPros[0] && <BuyingPros data={buyingPros[0]} className={styles.buyingPros} />}

      {claimsLinks?.length > 0 && (
        <>
          <h2 className={styles.productAttributesTitle}>{thisProductIs}</h2>
          <div className={styles.productAttributes}>
            {claimsLinks.map(link => (
              <AttributeTag className={styles.attributeTag} key={link.title} link={link} locale={locale} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ProductInfo;
